import { useEffect, useMemo, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";

/**
 * Data model expected from the parent:
 * roles = [
 *   {
 *     id: string | number,
 *     name: string,
 *     users: [{ id: string | number, name: string }]
 *   }
 * ]
 *
 * selectedUserIds = array of user ids (string | number)
 */
const RoleUserSelectionModal = ({ shown, roles = [], selectedUserIds = [], onCancel, onSave }) => {
  const [checkedUsers, setCheckedUsers] = useState({});
  const [expandedRoles, setExpandedRoles] = useState({});

  // initialize check states whenever modal opens or data changes
  useEffect(() => {
    if (!shown) return;

    const initialChecks = {};
    roles.forEach((role) => {
      role.users?.forEach((user) => {
        initialChecks[user.id] = selectedUserIds.includes(String(user.id)) || selectedUserIds.includes(user.id);
      });
    });
    setCheckedUsers(initialChecks);

    const initialExpanded = {};
    roles.forEach((role) => {
      initialExpanded[role.id] = true;
    });
    setExpandedRoles(initialExpanded);
  }, [shown, roles, selectedUserIds]);

  const toggleRoleExpand = (roleId) => {
    setExpandedRoles((prev) => ({ ...prev, [roleId]: !prev[roleId] }));
  };

  const setRoleChecked = (role, isChecked) => {
    const next = { ...checkedUsers };
    role.users?.forEach((user) => {
      next[user.id] = isChecked;
    });
    setCheckedUsers(next);
  };

  const setUserChecked = (userId, isChecked) => {
    setCheckedUsers((prev) => ({ ...prev, [userId]: isChecked }));
  };

  const roleState = useMemo(() => {
    const state = {};
    roles.forEach((role) => {
      const total = role.users?.length || 0;
      const selectedCount = role.users?.reduce((acc, user) => acc + (checkedUsers[user.id] ? 1 : 0), 0) || 0;
      state[role.id] = {
        checked: total > 0 && selectedCount === total,
        indeterminate: selectedCount > 0 && selectedCount < total,
      };
    });
    return state;
  }, [roles, checkedUsers]);

  const handleSave = () => {
    const selectedUsers = [];
    roles.forEach((role) => {
      role.users?.forEach((user) => {
        if (checkedUsers[user.id]) {
          selectedUsers.push(user);
        }
      });
    });
    onSave?.(selectedUsers);
  };

  if (!shown) return null;

  return (
    <div className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-30">
      <OutsideClickHandler onOutsideClick={() => onCancel?.()}>
        <div className="w-[420px] h-[520px] rounded-[6px] bg-white p-[18px] flex flex-col gap-3 shadow">
          <p className="font-OpenSansBold text-[16px] text-titleColor text-center">Select Users By Role</p>

          <div className="flex-1 overflow-auto w-full rounded-[6px] p-3">
            {roles.map((role) => {
              const currentRoleState = roleState[role.id] || { checked: false, indeterminate: false };
              return (
                <div key={role.id} className="mb-3">
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => toggleRoleExpand(role.id)} className="text-titleColor">
                      {expandedRoles[role.id] ? <MdKeyboardArrowUp size={18} /> : <MdKeyboardArrowDown size={18} />}
                    </button>
                    <input
                      type="checkbox"
                      checked={currentRoleState.checked}
                      ref={(el) => {
                        if (el) el.indeterminate = currentRoleState.indeterminate;
                      }}
                      onChange={(e) => setRoleChecked(role, e.target.checked)}
                    />
                    <p className="font-OpenSansMedium text-[14px] text-titleColor">{`${role.name}s`}</p>
                  </div>

                  {expandedRoles[role.id] && (
                    <div className="pl-10 pt-2 flex flex-col gap-2">
                      {role.users?.map((user) => (
                        <label key={user.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!checkedUsers[user.id]}
                            onChange={(e) => setUserChecked(user.id, e.target.checked)}
                          />
                          <span className="font-OpenSansRegular text-[13px] text-black">{user.name}</span>
                        </label>
                      ))}
                      {!role.users?.length && (
                        <p className="font-OpenSansRegular text-[12px] text-gray-400 italic">No users</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {!roles.length && <p className="font-OpenSansRegular text-[13px] text-gray-500">No roles available.</p>}
          </div>

          <div className="w-full flex justify-between gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="w-[50%] h-[30px] rounded-[6px] flex items-center justify-center font-OpenSansRegular font-[600] text-[10px] text-inputFieldTextColor border-borderColor border-[1px]"
            >
              CANCEL
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="w-[50%] h-[30px] bg-blueColor rounded-[6px] flex items-center justify-center font-OpenSansRegular font-[600] text-[10px] text-white"
            >
              SAVE
            </button>
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
};

export default RoleUserSelectionModal;
